import { ToastContent, useToast } from "@/components/ui/use-toast";
import {
  isReportOutOfPolicyState,
  outOfPolicyState,
} from "@common/state/outOfPolicyState";
import {
  ExpenseReportsByViewDocument,
  ReportPolicyCheckerResult,
  SubmitDraftExpenseReportResult,
  useSubmitDraftExpenseReportMutation,
} from "@common/utils/graphql";
import { ExtractTypename } from "@common/utils/graphql/utils";
import { useState } from "react";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";

type SubmittedProps = ExtractTypename<
  SubmitDraftExpenseReportResult,
  "SubmittedReportResult"
>;

type OutOfPolicyProps = ExtractTypename<
  ReportPolicyCheckerResult,
  "ReportPolicyCheckerResult"
>;

export const useSubmitDraftReport = (
  reportId: number,
  onDraftReportSubmitted?: () => void,
) => {
  const { toast } = useToast();
  const setOutOfPolicyState = useSetRecoilState(outOfPolicyState);
  const resetOutOfPolicyState = useResetRecoilState(outOfPolicyState);
  const isReportOutOfPolicy = useRecoilValue(
    isReportOutOfPolicyState(reportId),
  );
  const [isHidden, setIsHidden] = useState(false);
  const [submitDraftExpenseReport, { loading }] =
    useSubmitDraftExpenseReportMutation({
      refetchQueries: [ExpenseReportsByViewDocument],
    });

  const onSubmitted = () => {
    setIsHidden(true);
    onDraftReportSubmitted?.();
    // We only track one out of policy report at a time, so we always reset the state after a report is submitted
    resetOutOfPolicyState();
  };

  const getSubmittedToastMessage = (data: SubmittedProps): ToastContent => {
    if (data.report.isAutoApproved) {
      return { title: "Report Auto-Approved!", variant: "default" };
    }
    const toastContent: ToastContent = {
      title: "Manager Review Required",
      description:
        "The report was not auto-approved due to a policy violation.",
      variant: "warning",
    };
    const { reason } = data.autoApprovalMetadata;
    if (reason) {
      toastContent.reason = reason;
    }
    return toastContent;
  };

  // When a report is out of policy, we store reportId along with key/value pairs of out of policy expenseIds and the reasons
  // This will replace any existing out of policy state
  const handleOutOfPolicyState = (data: OutOfPolicyProps) => {
    const expenseIdToReasonMap = data.policyViolations.reduce(
      (acc, { expenseId, reason }) => ({ ...acc, [expenseId]: reason }),
      {},
    );
    setOutOfPolicyState({ expenseIdToReasonMap, reportId: reportId });
  };

  const onSubmitDraftReport = async () => {
    let errorToastMessage = undefined;
    try {
      const { data } = await submitDraftExpenseReport({
        variables: {
          reportId: reportId,
          skipPolicyCheck: isReportOutOfPolicy,
        },
      });

      const responseTypeName = data?.submitDraftExpenseReport.__typename;

      // An error occurred
      if (
        !data ||
        responseTypeName !== "MutationSubmitDraftExpenseReportSuccess"
      ) {
        if (data && responseTypeName === "BaseError") {
          errorToastMessage = data.submitDraftExpenseReport.message;
        }
        throw new Error();
      }
      const submitDraftData = data.submitDraftExpenseReport.data;

      // Report was submitted
      if (submitDraftData.__typename === "SubmittedReportResult") {
        onSubmitted();
        const { title, description, variant, reason } =
          getSubmittedToastMessage(submitDraftData as SubmittedProps);
        toast({ title, description, variant, reason });
      }

      // AI detected out of policy expenses
      if (submitDraftData.__typename === "ReportPolicyCheckerResult") {
        handleOutOfPolicyState(submitDraftData as OutOfPolicyProps);
        toast({
          title: "Report has expenses that are out of policy",
          description:
            "Please update the expenses or submit anyways for manager review.",
          variant: "destructive",
        });
      }
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to submit report, please try again",
        description: errorToastMessage,
      });
    }
  };

  return {
    onSubmitDraftReport,
    isHidden,
    loading,
    isReportOutOfPolicy,
  };
};
